import { useCallback, useContext } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useLogGoodsAnalyticsEvent } from '../../../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { bridge, isWeb } from 'shared/src/utils';
import { useSelectOptionContext } from '../context/SelectOptionsContext';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { GoodsParamsContext } from '../../../context/GoodsParamsContext';

export const useLogClickAddToCart = (params: { selected: GoodsOption[] }) => {
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  const { mode: selectOptionMode } = useSelectOptionContext();
  const injected = useContext(GoodsParamsContext);
  const sno = useGoodsSno();
  // 찜탭 등 route 밖에서는 카드 데이터로 주입된 값을, PDP 에서는 상세 데이터를 사용.
  const folderSno = injected?.logging
    ? injected.logging.folderSno
    : goodsDetail?.goods.like?.folder_sno;

  return useCallback(() => {
    const totalOptionPrice = params.selected.reduce(
      (val, cur) => val + cur.price * cur.stock,
      0,
    );
    const totalQuantity = params.selected.reduce(
      (acc, cur) => acc + cur.stock,
      0,
    );

    const isDelayPercent =
      (params.selected.filter(option => option.is_delayed).length /
        params.selected.length) *
      100;

    logGoodsAnalytics({
      name: 'CLICK_ADD_TO_CART',
      analytics: {
        ADD_GOODS_COUNT: params.selected.length,
        FOLDER_TYPE: folderSno !== 0 ? '제작서립' : '기본서랍',
        TOTAL_OPTIONS_PRICE: totalOptionPrice,
        IS_DELAY_PERCENT: Math.ceil(isDelayPercent),
        ...(selectOptionMode === 'gift' ? { KIND: 'GIFT' } : {}),
      },
    });

    if (isWeb) {
      bridge.logAnalyticsEvent({
        name: 'AddToCart',
        channel: ['pixel'],
        analytics: {
          content_ids: [goodsBasic?.sno],
          content_category: goodsBasic?.standard_category.name,
          content_name: goodsBasic?.name,
          content_type: 'product',
          currency: 'KRW',
          value: totalOptionPrice,
          contents: [
            {
              id: goodsBasic?.sno,
              quantity: totalQuantity,
            },
          ],
        },
      });
    }

    params.selected.forEach(selected => {
      bridge.logAnalyticsEvent({
        name: 'CLICK_ADD_TO_CART_PER_OPTION',
        analytics: {
          EVENT_TYPE: 'CLICK_ADD_TO_CART_PER_OPTION',
          ADD_GOODS_OPTION_DELIVERY_TYPE: selected.delivery_type,
          GOODS_SNO: (injected?.logging
            ? sno
            : goodsDetail?.goods.sno
          )?.toString(),
          IS_DELAY: selected.is_delayed ? '1' : '0',
          OPTION_SNO: selected.sno.toString(),
          SCREEN: injected?.logging ? injected.logging.screen : 'GOODS_DETAIL',
          ...(selectOptionMode === 'gift' ? { KIND: 'GIFT' } : {}),
        },
      });
    });
  }, [
    folderSno,
    goodsBasic?.name,
    goodsBasic?.sno,
    goodsBasic?.standard_category.name,
    goodsDetail?.goods.sno,
    injected,
    sno,
    logGoodsAnalytics,
    params.selected,
    selectOptionMode,
  ]);
};
